import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Barcode, Check, Sparkles, Star } from 'lucide-react';
import { barcodeProblem, type EnteredBarcodeType } from '@optiflow/shared';
import { BarcodeType, type ProductVariantSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { apiErrorCode, apiErrorDetails } from '@/services/apiClient';
import {
  useAddBarcodeMutation,
  useGenerateBarcodeMutation,
  useMakeBarcodePrimaryMutation,
  useRemoveBarcodeMutation,
} from '../api/catalog.api';

interface BarcodeManagerProps {
  productId: string;
  variant: ProductVariantSummary;
  canEdit: boolean;
  onFeedback: (message: string) => void;
}

/**
 * Barcodes on one variant.
 *
 * A variant normally carries the manufacturer code from the box, or an internal Code 128 issued on
 * request for stock that arrived unlabelled. Any of them scans at the till; the primary is what gets
 * printed on a label. A withdrawn code is retired, never deleted: it keeps scanning to this variant
 * and is never given to another one.
 */
export function BarcodeManager({ productId, variant, canEdit, onFeedback }: BarcodeManagerProps) {
  const { t } = useTranslation();

  const [code, setCode] = useState('');
  const [type, setType] = useState<string>(BarcodeType.EAN_13);
  const [error, setError] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

  const addBarcode = useAddBarcodeMutation(productId, variant.id);
  const makePrimary = useMakeBarcodePrimaryMutation(productId, variant.id);
  const removeBarcode = useRemoveBarcodeMutation(productId, variant.id);
  const generate = useGenerateBarcodeMutation(productId, variant.id);
  const active = variant.barcodes.filter((barcode) => barcode.retiredAt === null);

  /** The server's refusals, in the reader's language: never its English sentence. */
  const explain = (caught: unknown, fallback: string) => {
    const detail = apiErrorDetails(caught)[0]?.message ?? '';
    const code = apiErrorCode(caught);
    setError(
      code === 'CONFLICT'
        ? t('catalog.barcodes.taken')
        : detail === 'invalid check digit'
          ? t('catalog.barcodes.checkDigit')
          : detail.startsWith('invalid ')
            ? t('catalog.barcodes.badLength')
            : fallback,
    );
  };

  const typeOptions = Object.values(BarcodeType).map((value) => ({
    value,
    label: t(`catalog.barcodeTypes.${value}`),
  }));

  const handleAdd = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const trimmed = code.trim();
    if (trimmed.length < 4) {
      setError(t('catalog.barcodes.tooShort'));
      return;
    }
    const problem = barcodeProblem(trimmed, type as EnteredBarcodeType);
    if (problem) {
      setError(
        problem === 'CHECK_DIGIT'
          ? t('catalog.barcodes.checkDigit')
          : t('catalog.barcodes.badLength'),
      );
      return;
    }

    addBarcode.mutate(
      { code: trimmed, type },
      {
        onSuccess: () => {
          setCode('');
          onFeedback(t('catalog.barcodes.added'));
        },
        onError: (caught) => explain(caught, t('catalog.barcodes.addError')),
      },
    );
  };

  return (
    <div className="space-y-3">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {canEdit && active.length === 0 ? (
        <div className="border-line flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-3">
          <p className="text-muted text-sm">{t('catalog.barcodes.none')}</p>
          <Button
            variant="secondary"
            size="sm"
            isLoading={generate.isPending}
            onClick={() =>
              generate.mutate(undefined, {
                onSuccess: () => onFeedback(t('catalog.barcodes.generated')),
                onError: (caught) => explain(caught, t('catalog.barcodes.generateError')),
              })
            }
          >
            <Sparkles className="size-4" aria-hidden />
            {t('catalog.barcodes.generate')}
          </Button>
        </div>
      ) : null}

      {variant.barcodes.length === 0 && !canEdit ? (
        <p className="text-muted text-sm">{t('catalog.barcodes.none')}</p>
      ) : null}

      {variant.barcodes.length > 0 ? (
        <ul className="divide-line border-line divide-y rounded-lg border">
          {variant.barcodes.map((barcode) => (
            <li key={barcode.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
              <Barcode className="text-muted size-4 shrink-0" aria-hidden />

              <span className="min-w-0 flex-1">
                <span className="text-ink block truncate font-mono text-sm" dir="ltr">
                  {barcode.code}
                </span>
                <span className="text-muted text-xs">
                  {t(`catalog.barcodeTypes.${barcode.type}`)}
                  {barcode.type === BarcodeType.CODE_128
                    ? ` · ${t('catalog.barcodes.internal')}`
                    : ''}
                </span>
              </span>

              {barcode.retiredAt !== null ? (
                <span className="bg-canvas text-muted inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium">
                  <Archive className="size-3" aria-hidden />
                  {t('catalog.barcodes.retired')}
                </span>
              ) : barcode.isPrimary ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  <Check className="size-3" aria-hidden />
                  {t('catalog.barcodes.primary')}
                </span>
              ) : canEdit ? (
                <Button
                  variant="ghost"
                  size="sm"
                  isLoading={makePrimary.isPending}
                  onClick={() =>
                    makePrimary.mutate(barcode.id, {
                      onSuccess: () => onFeedback(t('catalog.barcodes.primaryChanged')),
                    })
                  }
                >
                  <Star className="size-3.5" aria-hidden />
                  {t('catalog.barcodes.makePrimary')}
                </Button>
              ) : null}

              {canEdit && barcode.retiredAt === null && active.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setPendingRemoval(barcode.id)}
                  aria-label={t('catalog.barcodes.retireNamed', { code: barcode.code })}
                  title={t('catalog.barcodes.retire')}
                  className="text-muted shrink-0 hover:text-red-600"
                >
                  <Archive className="size-4" aria-hidden />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {canEdit ? (
        <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-2">
          <div className="min-w-40 flex-1">
            <Input
              label={t('catalog.barcodes.addLabel')}
              value={code}
              dir="ltr"
              onChange={(event) => setCode(event.target.value)}
              placeholder="8053672000871"
            />
          </div>
          <div className="w-36">
            <Select
              options={typeOptions}
              value={type}
              aria-label={t('catalog.barcodes.type')}
              onChange={(event) => setType(event.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary" isLoading={addBarcode.isPending}>
            {t('catalog.barcodes.add')}
          </Button>
        </form>
      ) : null}

      <ConfirmDialog
        isOpen={pendingRemoval !== null}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) return;
          removeBarcode.mutate(pendingRemoval, {
            onSuccess: () => {
              setPendingRemoval(null);
              onFeedback(t('catalog.barcodes.retiredDone'));
            },
            onError: (caught) => {
              explain(caught, t('catalog.barcodes.retireError'));
              setPendingRemoval(null);
            },
          });
        }}
        isConfirming={removeBarcode.isPending}
        isDestructive
        title={t('catalog.barcodes.retireTitle')}
        message={t('catalog.barcodes.retireMessage')}
        confirmLabel={t('catalog.barcodes.retire')}
      />
    </div>
  );
}
